import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Tag,
  Card,
  Button,
  Space,
  Modal,
  Typography,
  Form,
  Select,
  DatePicker,
  Divider,
  Badge,
  Descriptions,
  message,
  Drawer,
  Input,
  Tooltip,
  Dropdown,
  Row,
  Col,
} from "antd";
import {
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  SearchOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  DollarOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import {
  getTransactions,
  getTransactionById,
  updateTransactionStatus,
} from "../../services/transactionService";
import { createReport } from "../../services/reportService";
import { pusherService } from "../../services/pusherService";
import TransactionDocumentDrawer from "./TransactionDocumentDrawer";
import ReportGenerationModal from "./ReportGenerationModal";
import { formatCurrency, formatDate } from "../../utils/formatters";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TransactionList = ({
  transactions: initialTransactions,
  loading,
  hasMore,
  onLoadMore,
  onRefresh,
  onFilter,
  filters,
  isSmallScreen,
  observerTarget,
  hideFilterAndRefresh = false,
}) => {
  const [transactions, setTransactions] = useState(initialTransactions);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [form] = Form.useForm();
  const [declineForm] = Form.useForm();
  const [statusLoading, setStatusLoading] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  
  // Document review state
  const [documentReviewVisible, setDocumentReviewVisible] = useState(false);
  const [selectedTransactionForReview, setSelectedTransactionForReview] = useState(null);

  // Update transactions when initialTransactions changes
  useEffect(() => {
    setTransactions(initialTransactions);
  }, [initialTransactions]);

  // Initialize Pusher and handle report notifications
  useEffect(() => {
    const initializePusher = async () => {
      try {
        // Check if user is authenticated before initializing Pusher
        const token = sessionStorage.getItem('auth_token');
        if (!token) {
          console.warn('No auth token found, skipping Pusher initialization');
          return;
        }

        await pusherService.initialize();
        pusherService.subscribeToReports((data) => {
          console.log('Report notification received:', data);
          if (data.status === 'completed' && data.file_url) {
            // Trigger file download
            const link = document.createElement('a');
            link.href = data.file_url;
            link.download = data.filename || 'report.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success(`Report "${data.filename}" is ready for download!`);
          } else if (data.status === 'failed') {
            message.error('Report generation failed. Please try again.');
          }
        });
      } catch (error) {
        console.error('Failed to initialize Pusher:', error);
        // Don't throw the error, just log it so the component can still work
      }
    };

    // Add a small delay to ensure the component is fully mounted
    const timeoutId = setTimeout(initializePusher, 100);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
      pusherService.unsubscribeFromReports();
      pusherService.disconnect();
    };
  }, []);

  // Update form values when filters change
  useEffect(() => {
    form.setFieldsValue(filters);
  }, [filters, form]);

  // Handle filter form submission
  const handleFilterSubmit = useCallback(
    (values) => {
      // Clean up the values by removing empty/undefined values
      const cleanValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== "" && value !== null) {
          // Don't apply any transformation to dateRange
          acc[key] = value;
        }
        return acc;
      }, {});

      // Apply filters
      onFilter(cleanValues);
      setFilterVisible(false);
    },
    [onFilter]
  );

  // Reset filters
  const handleResetFilters = useCallback(() => {
    // Just reset form fields visually without any side effects
    form.resetFields();

    // Use a more direct approach to clear all fields
    const emptyValues = {
      reference: undefined,
      user_id: undefined,
      profile_id: undefined,
      user_name: undefined,
      dateRange: undefined,
      type: undefined,
      status: undefined,
      disburse_type: undefined,
    };

    form.setFieldsValue(emptyValues);

    // After clearing, trigger a refresh to reload all transactions
    if (onRefresh) {
      onRefresh();
    }
  }, [form, onRefresh]);

  // View transaction details
  const viewTransactionDetails = useCallback(async (id) => {
    try {
      setDetailLoading(true);
      const transaction = await getTransactionById(id);
      setSelectedTransaction(transaction);
      setDetailVisible(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      message.error("Failed to load transaction details");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Approve or decline transaction
  const handleStatusChange = useCallback(
    async (id, action) => {
      try {
        setStatusLoading(true);

        // In production, this would call the API
        // For now, we're using mock data
        await updateTransactionStatus(id, action);

        // Update the transaction in the list
        setTransactions((prev) =>
          prev.map((tx) =>
            tx.id === id
              ? {
                  ...tx,
                  status: action === "approve" ? "approved" : "declined",
                }
              : tx
          )
        );

        // If the transaction is currently being viewed, update it
        if (selectedTransaction && selectedTransaction.id === id) {
          setSelectedTransaction((prev) => ({
            ...prev,
            status: action === "approve" ? "approved" : "declined",
          }));
        }

        message.success(
          `Transaction ${
            action === "approve" ? "approved" : "declined"
          } successfully`
        );
      } catch (error) {
        console.error(`Error ${action}ing transaction:`, error);
        message.error(`Failed to ${action} transaction`);
      } finally {
        setStatusLoading(false);
      }
    },
    [selectedTransaction]
  );

  // Handle decline transaction
  const handleDecline = async (id, reason) => {
    try {
      setStatusLoading(true);
      await updateTransactionStatus(id, "decline", reason);

      // Update the transaction in the list
      setTransactions((prev) =>
        prev.map((tx) => (tx.id === id ? { ...tx, status: "declined" } : tx))
      );

      // If the transaction is currently being viewed, update it
      if (selectedTransaction && selectedTransaction.id === id) {
        setSelectedTransaction((prev) => ({
          ...prev,
          status: "declined",
        }));
      }

      message.success("Transaction declined successfully");
    } catch (error) {
      console.error("Error declining transaction:", error);
      message.error("Failed to decline transaction");
    } finally {
      setStatusLoading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusMap = {
      "pending-payment": "processing",
      "pending-approval": "warning",
      approved: "success",
      declined: "error",
      "pending-disburse": "processing",
      "payment-failed": "error",
      "disburse-failed": "error",
      completed: "success",
      "pending-refund": "warning",
      "refund-failed": "error",
      refunded: "default",
    };

    return statusMap[status] || "default";
  };

  // Get status display text with cash pickup context
  const getStatusDisplayText = (status, disburseType) => {
    const statusTextMap = {
      "pending-payment": "Pending Payment",
      "pending-approval": "Pending Approval",
      approved: "Approved",
      declined: "Declined",
      "pending-disburse": "Pending Disburse",
      "payment-failed": "Payment Failed",
      "disburse-failed": "Disbursement Failed",
      completed: "Completed",
      "pending-refund": "Pending Refund",
      "refund-failed": "Refund Failed",
      refunded: "Refunded",
    };

    return statusTextMap[status] || status;
  };

  // Format transaction type for display
  const formatTransactionType = (type) => {
    if (!type) return "";

    // Convert kebab-case to Title Case
    return type
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper to determine if filters are active
  const hasActiveFilters = useMemo(() => {
    if (!filters) return false;

    // Count filters that have values
    const activeFilterCount = Object.keys(filters).reduce((count, key) => {
      const value = filters[key];
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return count; // Skip empty values
      }
      return count + 1;
    }, 0);

    return activeFilterCount > 0;
  }, [filters]);

  // Handle local refresh that includes scrolling to top
  const handleRefreshWithScroll = useCallback(() => {
    // First scroll the container to top
    const container = document.querySelector(".transaction-table-container");
    if (container) {
      container.scrollTop = 0;
    }

    // Then call the parent refresh function
    onRefresh();
  }, [onRefresh]);

  // Document review functions
  const openDocumentReview = useCallback((transaction) => {
    setSelectedTransactionForReview(transaction);
    setDocumentReviewVisible(true);
  }, []);

  const closeDocumentReview = useCallback(() => {
    setDocumentReviewVisible(false);
    setSelectedTransactionForReview(null);
  }, []);

  const handleDocumentApproval = useCallback((documentKey, status, reason) => {
    console.log('Document approval:', { documentKey, status, reason });
    // TODO: Implement API call to update document status
    // This will be called when admin approves/declines a document
  }, []);

  const handleTransactionApproval = useCallback(async (action, reason) => {
    try {
      setStatusLoading(true);
      
      let newStatus;
      if (action === "approve") {
        newStatus = "approved";
      } else if (action === "decline") {
        newStatus = "declined";
      } else if (action === "cash-ready-success") {
        newStatus = "pending-disburse";
      } else if (action === "cash-collected-success") {
        newStatus = "completed";
      } else {
        newStatus = action;
      }
      
      // Only call updateTransactionStatus for approve/decline actions
      // Cash pickup actions are handled separately in TransactionDocumentDrawer
      if (action === "approve" || action === "decline") {
        await updateTransactionStatus(selectedTransactionForReview.id, action, reason);
      }
      
      // Update the transaction in the list
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === selectedTransactionForReview.id
            ? {
                ...tx,
                status: newStatus,
              }
            : tx
        )
      );

      // Don't show success message for cash pickup actions as they're already shown in the drawer
      if (action !== "cash-ready-success" && action !== "cash-collected-success") {
        let successMessage;
        if (action === "mark-cash-ready") {
          successMessage = "Cash marked as ready for pickup successfully";
        } else if (action === "mark-cash-collected") {
          successMessage = "Cash marked as collected successfully";
        } else {
          successMessage = `Transaction ${action === "approve" ? "approved" : "declined"} successfully`;
        }
        
        message.success(successMessage);
      }
      
      closeDocumentReview();
    } catch (error) {
      console.error("Error updating transaction status:", error);
      message.error(`Failed to ${action} transaction`);
    } finally {
      setStatusLoading(false);
    }
  }, [selectedTransactionForReview, closeDocumentReview]);

  // Table columns
  const columns = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      width: 150,
      render: (text) => <Text copyable>{text}</Text>,
    },
    {
      title: "User",
      dataIndex: "user_name",
      key: "user_name",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => formatTransactionType(type),
    },
    {
      title: "Amount",
      key: "amount",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {formatCurrency(record.source_amount, record.source_currency_code)}
          </Text>
          <Text type="secondary">
            {formatCurrency(record.dest_amount, record.dest_currency_code)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => (
        <Tag color={getStatusColor(status)}>
          {getStatusDisplayText(status, record.disburse_type)}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "created",
      key: "created",
      width: 150,
      render: (timestamp) => formatDate(timestamp),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            icon: <EyeOutlined />,
            label: "View Full Transaction Details",
            onClick: () => viewTransactionDetails(record.id),
          },
        ];

        // Add document review action for pending-approval transactions
        if (record.status === "pending-approval") {
          menuItems.push({
            key: "view-documents",
            icon: <FileTextOutlined style={{ color: "#1890ff" }} />,
            label: "View Documents",
            onClick: () => openDocumentReview(record),
          });
        }

        // Add cash pickup actions for cash transactions
        if (record.disburse_type === "cash") {
          if (record.status === "approved") {
            menuItems.push({
              key: "mark-cash-ready",
              icon: <DollarOutlined style={{ color: "#52c41a" }} />,
              label: "Mark Cash Ready",
              onClick: () => openDocumentReview(record),
            });
          } else if (record.status === "pending-disburse") {
            menuItems.push({
              key: "mark-cash-collected",
              icon: <CheckOutlined style={{ color: "#52c41a" }} />,
              label: "Mark Cash Collected",
              onClick: () => openDocumentReview(record),
            });
          }
        }

        return (
          <Dropdown
            menu={{
              items: menuItems,
            }}
            trigger={["click"]}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ border: "none" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  // ------------------------
  // Export helpers (CSV)
  // ------------------------

  const buildCsv = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return '';
    }

    // Define the columns we want to export and their headers
    const headers = [
      'Transaction ID',
      'Reference',
      'Customer Name',
      'Type',
      'Status',
      'Source Amount',
      'Source Currency',
      'Destination Amount',
      'Destination Currency',
      'Payment Type',
      'Disbursement Type',
      'Created At',
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const lines = [];
    lines.push(headers.join(','));

    rows.forEach((tx) => {
      const line = [
        escapeCsv(tx.id),
        escapeCsv(tx.reference),
        escapeCsv(tx.user_name),
        escapeCsv(formatTransactionType(tx.type)),
        escapeCsv(getStatusDisplayText(tx.status, tx.disburse_type)),
        escapeCsv(tx.source_amount),
        escapeCsv(tx.source_currency_code),
        escapeCsv(tx.dest_amount),
        escapeCsv(tx.dest_currency_code),
        escapeCsv(formatTransactionType(tx.payment_type)),
        escapeCsv(formatTransactionType(tx.disburse_type)),
        escapeCsv(formatDate(tx.created)),
      ].join(',');
      lines.push(line);
    });

    return lines.join('\n');
  }, []);

  const downloadBlob = useCallback((content, filename, mime = 'text/csv;charset=utf-8;') => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const exportCurrentPageCsv = useCallback(() => {
    try {
      const csv = buildCsv(transactions || []);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      downloadBlob(csv, `transactions-current-list-${ts}.csv`);
      message.success('Exported current page to CSV');
    } catch (err) {
      console.error('CSV export error:', err);
      message.error('Failed to export CSV');
    }
  }, [transactions, buildCsv, downloadBlob]);

  // Build API params from active filters
  const buildQueryParamsFromFilters = useCallback((activeFilters) => {
    const params = {};
    if (!activeFilters) return params;

    // date range -> from/to (align with Dashboard behavior)
    if (
      activeFilters.dateRange &&
      Array.isArray(activeFilters.dateRange) &&
      activeFilters.dateRange.length === 2
    ) {
      try {
        const startDate = new Date(activeFilters.dateRange[0]);
        const endDate = new Date(activeFilters.dateRange[1]);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const startOfDay = new Date(startDate);
          startOfDay.setHours(0, 0, 0, 0);
          params.from = startOfDay.valueOf();
          const now = new Date();
          const isToday =
            endDate.getFullYear() === now.getFullYear() &&
            endDate.getMonth() === now.getMonth() &&
            endDate.getDate() === now.getDate();
          if (isToday) {
            params.to = now.valueOf();
          } else {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            params.to = endOfDay.valueOf();
          }
        }
      } catch (_) {}
    }

    if (activeFilters.type) {
      params.type = Array.isArray(activeFilters.type)
        ? activeFilters.type.join(',')
        : activeFilters.type;
    }
    if (activeFilters.status) {
      params.status = Array.isArray(activeFilters.status)
        ? activeFilters.status.join(',')
        : activeFilters.status;
    }
    if (activeFilters.disburse_type) {
      params.disburse_type = Array.isArray(activeFilters.disburse_type)
        ? activeFilters.disburse_type.join(',')
        : activeFilters.disburse_type;
    }
    if (activeFilters.reference) params.reference = activeFilters.reference.trim();
    if (activeFilters.user_id) params.user_id = activeFilters.user_id.trim();
    if (activeFilters.profile_id) params.profile_id = activeFilters.profile_id.trim();
    if (activeFilters.user_name) params.user_name = activeFilters.user_name.trim();

    return params;
  }, []);

  const exportAllMatchingCsv = useCallback(async () => {
    const hide = message.loading('Exporting all matching transactions...', 0);
    try {
      const LIMIT = 200;
      const all = [];
      let next_cursor = '';
      const baseParams = buildQueryParamsFromFilters(filters || {});

      // If searching specific ids/refs, avoid cursor to let backend return full set
      const isSearching = !!(
        baseParams.reference || baseParams.user_id || baseParams.profile_id || baseParams.user_name
      );

      // Paginate until no next_cursor
      // Ensure we always pass limit to reduce number of calls
      // Guardrail: cap total rows to avoid runaway memory in the browser
      const MAX_ROWS = 25000;

      while (true) {
        const params = { ...baseParams, limit: LIMIT };
        if (!isSearching && next_cursor) {
          params.next_cursor = next_cursor;
        }
        // On first call, next_cursor is empty string per hook default; omit it
        if (params.next_cursor === '') delete params.next_cursor;

        // eslint-disable-next-line no-await-in-loop
        const res = await getTransactions(params);
        const page = Array.isArray(res?.transactions) ? res.transactions : [];
        all.push(...page);
        if (all.length >= MAX_ROWS) {
          break;
        }
        next_cursor = res?.next_cursor;
        if (!next_cursor || isSearching) {
          break;
        }
      }

      const trimmed = all.slice(0, MAX_ROWS);
      const csv = buildCsv(trimmed);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      downloadBlob(csv, `transactions-full-list-${ts}.csv`);
      message.success(`Exported ${trimmed.length} rows to CSV`);
    } catch (err) {
      console.error('CSV export (all) error:', err);
      message.error('Failed to export all matching rows');
    } finally {
      hide();
    }
  }, [filters, buildQueryParamsFromFilters, buildCsv, downloadBlob]);

  // Report generation functions
  const handleGenerateReport = useCallback(async (reportParams) => {
    if (reportGenerating) return;
    
    // Check if user is authenticated
    const token = sessionStorage.getItem('auth_token');
    if (!token) {
      message.error('Please log in to generate reports');
      return;
    }
    
    setReportGenerating(true);
    const hide = message.loading('Generating report...', 0);
    
    try {
      const reportRequest = {
        type: 'transactions-log',
        query: reportParams
      };
      
      const report = await createReport(reportRequest);
      
      // Check if report is already completed
      if (report.status === 'completed' && report.file_url) {
        // Report completed immediately, download it now
        const link = document.createElement('a');
        link.href = report.file_url;
        link.download = report.filename || 'report.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(`Report "${report.filename}" downloaded successfully!`);
      } else {
        // Report is pending, will be notified via Pusher
        message.success('Report generation started. You will be notified when ready.');
      }
      
      setReportModalVisible(false);
    } catch (err) {
      console.error('Report generation error:', err);
      if (err.response?.data?.code === 'INVALID_AUTH_TOKEN') {
        message.error('Your session has expired. Please log in again.');
      } else {
        message.error('Failed to generate report');
      }
    } finally {
      hide();
      setReportGenerating(false);
    }
  }, [reportGenerating]);

  const handleOpenReportModal = useCallback(() => {
    setReportModalVisible(true);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setReportModalVisible(false);
  }, []);

  return (
    <>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Transactions
            </Title>
          </Space>
        }
        extra={
          !hideFilterAndRefresh ? (
            <Space>
              <Badge dot={hasActiveFilters} offset={[-5, 5]} color="#1890ff">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFilterVisible(true)}
                  type={hasActiveFilters ? "primary" : "default"}
                >
                  Filter{" "}
                  {hasActiveFilters
                    ? `(${
                        Object.keys(filters).filter(
                          (key) =>
                            filters[key] !== undefined &&
                            filters[key] !== null &&
                            filters[key] !== "" &&
                            !(
                              Array.isArray(filters[key]) &&
                              filters[key].length === 0
                            )
                        ).length
                      })`
                    : ""}
                </Button>
              </Badge>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleOpenReportModal}
                loading={reportGenerating}
              >
                {reportGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
              <Button onClick={handleRefreshWithScroll} loading={loading}>
                Refresh
              </Button>
            </Space>
          ) : null
        }
      >
        <div
          style={{
            height: "calc(100vh - 300px)",
            overflow: "auto",
            position: "relative",
          }}
          className="transaction-table-container"
        >
          <Table
            dataSource={transactions}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={loading}
            scroll={{ x: isSmallScreen ? 1200 : undefined }}
          />
          {/* Loading target for infinite scroll - adjusted to reduce ResizeObserver issues */}
          <div
            ref={observerTarget}
            style={{
              height: "30px",
              padding: "10px 0",
              marginTop: "5px",
              position: "relative",
              display: "block",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {hasMore && loading && (
              <div style={{ textAlign: "center" }}>
                <LoadingOutlined style={{ fontSize: 18 }} />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={null}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <LoadingOutlined style={{ fontSize: 24 }} />
          </div>
        ) : selectedTransaction ? (
          <div style={{ position: "relative" }}>
            <Badge.Ribbon
              text={formatTransactionType(selectedTransaction.status)}
              color={getStatusColor(selectedTransaction.status)}
              style={{ right: 0 }}
            >
              <Card>
                <Descriptions
                  title="Transaction Information"
                  column={1}
                  bordered
                >
                  <Descriptions.Item label="Transaction ID">
                    {selectedTransaction.id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reference">
                    {selectedTransaction.reference}
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    {formatTransactionType(selectedTransaction.type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    {selectedTransaction.description || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Source Amount">
                    {formatCurrency(
                      selectedTransaction.source_amount,
                      selectedTransaction.source_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Destination Amount">
                    {formatCurrency(
                      selectedTransaction.dest_amount,
                      selectedTransaction.dest_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Charge Amount">
                    {formatCurrency(
                      selectedTransaction.charge_amount,
                      selectedTransaction.source_currency_code
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Type">
                    {formatTransactionType(selectedTransaction.payment_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Disbursement Type">
                    {formatTransactionType(selectedTransaction.disburse_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {formatDate(selectedTransaction.created)}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Descriptions
                  title="Exchange Rate Information"
                  column={1}
                  bordered
                >
                  <Descriptions.Item label="Exchange Rate">
                    {`1 ${selectedTransaction.dest_currency_code} = ${(1 / selectedTransaction.exchange_rate).toFixed(2)} ${selectedTransaction.source_currency_code}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Exchange Rate">
                    {`1 ${selectedTransaction.dest_currency_code} = ${(1 / selectedTransaction.total_exchange_rate).toFixed(2)} ${selectedTransaction.source_currency_code}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Spread Rate">
                    {selectedTransaction.spread_rate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Spread Revenue">
                    {formatCurrency(
                      selectedTransaction.spread_revenue,
                      selectedTransaction.dest_currency_code
                    )}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {selectedTransaction.fee && (
                  <>
                    <Descriptions title="Fees" column={1} bordered>
                      {Object.entries(selectedTransaction.fee).map(
                        ([key, value]) => (
                          <Descriptions.Item
                            key={key}
                            label={key
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")}
                          >
                            {formatCurrency(
                              value,
                              selectedTransaction.source_currency_code
                            )}
                          </Descriptions.Item>
                        )
                      )}
                    </Descriptions>
                    <Divider />
                  </>
                )}

                <Descriptions title="User Information" column={1} bordered>
                  <Descriptions.Item label="User ID">
                    {selectedTransaction.user_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Business ID">
                    {selectedTransaction.profile_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Name">
                    {selectedTransaction.user_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedTransaction.user_email}
                  </Descriptions.Item>
                </Descriptions>

                {selectedTransaction.disburse_recipient_id && (
                  <>
                    <Divider />
                    <Descriptions
                      title="Recipient Information"
                      column={1}
                      bordered
                    >
                      <Descriptions.Item label="Recipient ID">
                        {selectedTransaction.disburse_recipient_id}
                      </Descriptions.Item>
                      <Descriptions.Item label="Name">
                        {selectedTransaction.disburse_recipient_name || "N/A"}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {selectedTransaction.status === "pending-approval" && (
                  <>
                    <Divider />
                    <Space style={{ marginTop: 16 }}>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() =>
                          handleStatusChange(selectedTransaction.id, "approve")
                        }
                        loading={statusLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: "Decline Transaction",
                            content: (
                              <Form form={declineForm} layout="vertical">
                                <Form.Item
                                  label="Reason"
                                  name="declineReason"
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please provide a reason for declining",
                                    },
                                  ]}
                                >
                                  <Input.TextArea rows={4} />
                                </Form.Item>
                              </Form>
                            ),
                            onOk: async (close) => {
                              try {
                                const values =
                                  await declineForm.validateFields();
                                await handleDecline(
                                  selectedTransaction.id,
                                  values.declineReason
                                );
                                close();
                              } catch (error) {
                                // Form validation failed
                                return false;
                              }
                            },
                          });
                        }}
                        loading={statusLoading}
                      >
                        Decline
                      </Button>
                    </Space>
                  </>
                )}
              </Card>
            </Badge.Ribbon>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Text disabled>Select a transaction to view details</Text>
          </div>
        )}
      </Modal>

      {/* Filter Drawer */}
      <Drawer
        title="Filter Transactions"
        placement="right"
        onClose={() => setFilterVisible(false)}
        open={filterVisible}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFilterSubmit}
          initialValues={filters}
        >
          {/* Commented out as requested
          <Form.Item name="user_id" label="User ID">
            <Input
              placeholder="Search by user ID"
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item name="profile_id" label="Business ID">
            <Input
              placeholder="Search by business ID"
              prefix={<BankOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>
          */}

          <Form.Item name="user_name" label="Customer Name">
            <Input
              placeholder="Search by customer name"
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item name="reference" label="Transaction Reference">
            <Input
              placeholder="Search by transaction reference"
              prefix={<SearchOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Date Range" name="dateRange">
            <RangePicker
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > new Date()}
              allowClear={true}
              inputReadOnly={true}
              placeholder={["Start date", "End date"]}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item label="Transaction Type" name="type">
            <Select
              mode="multiple"
              placeholder="Select transaction types"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="transfer">Transfer</Option>
              <Option value="credit">Credit</Option>
              <Option value="funding">Funding</Option>
              <Option value="virtual-card">Virtual Card</Option>
              <Option value="physical-card">Physical Card</Option>
              <Option value="airtime">Airtime</Option>
              <Option value="travel">Travel</Option>
              <Option value="e-sim">E-SIM</Option>
              <Option value="mobile-money">Mobile Money</Option>
              <Option value="pta">PTA</Option>
              <Option value="bta">BTA</Option>
              <Option value="medical-bills">Medical Bills</Option>
              <Option value="school-fees">School Fees</Option>
              <Option value="professional-exams">Professional Exams</Option>
              <Option value="others">Others</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select
              mode="multiple"
              placeholder="Select transaction statuses"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="pending-payment">Pending Payment</Option>
              <Option value="pending-approval">Pending Approval</Option>
              <Option value="approved">Approved</Option>
              <Option value="declined">Declined</Option>
              <Option value="pending-disburse">Pending Disburse</Option>
              <Option value="payment-failed">Payment Failed</Option>
              <Option value="disburse-failed">Disburse Failed</Option>
              <Option value="completed">Completed</Option>
              <Option value="pending-refund">Pending Refund</Option>
              <Option value="refund-failed">Refund Failed</Option>
              <Option value="refunded">Refunded</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Disbursement Type" name="disburse_type">
            <Select
              mode="multiple"
              placeholder="Select disbursement types"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="physical-card">Physical Card</Option>
              <Option value="virtual-card">Virtual Card</Option>
              <Option value="bank-account">Bank Account</Option>
              <Option value="wallet">Wallet</Option>
              <Option value="mobile-money">Mobile Money</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleResetFilters}>Clear Selections</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<FilterOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Transaction Document Review Drawer */}
      <TransactionDocumentDrawer
        visible={documentReviewVisible}
        transaction={selectedTransactionForReview}
        onClose={closeDocumentReview}
        onDocumentApproval={handleDocumentApproval}
        onTransactionApproval={handleTransactionApproval}
      />

      {/* Report Generation Modal */}
      <ReportGenerationModal
        visible={reportModalVisible}
        onCancel={handleCloseReportModal}
        onGenerate={handleGenerateReport}
        loading={reportGenerating}
      />
    </>
  );
};

export default TransactionList;
